import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, Subscription, timer } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { ApiService } from 'src/app/services/api/api.service';

import { LinkStatusGateService } from './link-status-gate.service';
import { NetworkContextService } from './network-context.service';
import { NetworkContext } from './network-context.types';
import { selectSpeedTestRunner, SpeedTestRunner } from './speed-test-runner';
import { SpeedTestScenarioService } from './speed-test-scenario.service';
import { SpeedTestService } from './speed-test.service';
import { isSpeedTestRunning, SpeedTestSnapshot, SpeedTestStatus } from './speed-test.types';

@Component({
  selector: 'app-speed-test',
  templateUrl: './speed-test.component.html',
  styleUrls: ['./speed-test.component.scss'],
})
export class SpeedTestComponent implements OnInit, OnDestroy {
  readonly snapshot$: Observable<SpeedTestSnapshot>;
  readonly speedTestServerName = environment.SPEEDTEST_SERVER_NAME;
  readonly speedTestServerLocation = environment.SPEEDTEST_SERVER_LOCATION;
  readonly preparationSteps = [
    {
      title: 'Aísle el router',
      detail:
        'Desconecte temporalmente la red LAN (switches y cables de red de la I.E.) del router de servicio.',
    },
    {
      title: 'Conecte en directo',
      detail:
        'Conecte la PC de prueba al router de servicio con un cable de red certificado (Cat 5e o Cat 6).',
    },
    {
      title: 'Apague el Wi-Fi',
      detail: 'Desactive el Wi-Fi del router de servicio.',
    },
    {
      title: 'Detenga descargas',
      detail:
        'Verifique que no haya actualizaciones o descargas en segundo plano en la PC de prueba.',
    },
    {
      title: 'Revise el equipo',
      detail: 'Compruebe que CPU, memoria, disco y red operen en condiciones óptimas.',
    },
  ] as const;
  readonly maintenanceMode = false;
  lastMeasuredAt: Date | null = null;
  networkContext: NetworkContext | null = null;
  networkContextLoading = false;
  recommendationsOpen = true;
  reportDownloading = false;
  canStart = false;
  linkChecking = false;
  @ViewChild('speedTestCard', { read: ElementRef }) speedTestCard?: ElementRef<HTMLElement>;
  private readonly runner: SpeedTestRunner;
  private snapshotSub?: Subscription;
  private networkContextSub?: Subscription;
  private servicesSub?: Subscription;
  private verifySub?: Subscription;
  private clientServiceId: number | null = null;

  constructor(
    liveSpeedTest: SpeedTestService,
    scenarioSpeedTest: SpeedTestScenarioService,
    private readonly networkContextService: NetworkContextService,
    private readonly api: ApiService,
    private readonly linkStatusGate: LinkStatusGateService,
  ) {
    this.runner = selectSpeedTestRunner(environment.SPEED_TEST_MODE, liveSpeedTest, scenarioSpeedTest);
    this.snapshot$ = this.runner.snapshot$;
  }

  ngOnInit(): void {
    if (this.maintenanceMode) {
      return;
    }

    this.snapshotSub = this.snapshot$.subscribe((snapshot) => {
      if (snapshot.status === 'completed') {
        this.lastMeasuredAt = new Date();
        this.recommendationsOpen = false;
      }
    });
    this.loadNetworkContext();
    this.loadLinkStatus();
  }

  ngOnDestroy(): void {
    this.snapshotSub?.unsubscribe();
    this.networkContextSub?.unsubscribe();
    this.servicesSub?.unsubscribe();
    this.verifySub?.unsubscribe();
    this.runner.dispose();
  }

  start(): void {
    if (this.maintenanceMode) {
      return;
    }

    this.recommendationsOpen = false;
    this.runVerify(() => {
      void this.runner.start();
    });
  }

  retryVerification(): void {
    if (this.maintenanceMode || this.linkChecking) {
      return;
    }

    this.runVerify();
  }

  toggleRecommendations(): void {
    this.recommendationsOpen = !this.recommendationsOpen;
  }

  cancel(): void {
    this.runner.abort();
  }

  async downloadReport(snapshot: SpeedTestSnapshot): Promise<void> {
    if (this.reportDownloading || snapshot.status !== 'completed') {
      return;
    }

    const host = this.speedTestCard?.nativeElement;
    const cardElement = host?.querySelector<HTMLElement>('.card') ?? host;
    if (!cardElement) {
      return;
    }

    this.reportDownloading = true;
    try {
      const { downloadSpeedTestPng } = await import('./speed-test-png');
      await downloadSpeedTestPng(cardElement, this.lastMeasuredAt ?? new Date());
    } finally {
      this.reportDownloading = false;
    }
  }

  isBusy(snapshot: SpeedTestSnapshot): boolean {
    return isSpeedTestRunning(snapshot.status);
  }

  hasPhaseProgress(status: SpeedTestStatus): boolean {
    return status === 'ping' || status === 'download' || status === 'upload';
  }

  actionLabel(status: SpeedTestStatus): string {
    if (isSpeedTestRunning(status)) {
      return 'Cancelar prueba';
    }
    if (status === 'completed') {
      return 'Repetir prueba';
    }
    if (status === 'failed') {
      return 'Reintentar prueba';
    }
    return 'Iniciar prueba';
  }

  phaseLabel(status: SpeedTestStatus): string {
    switch (status) {
      case 'preparing':
        return 'Preparando prueba...';
      case 'ping':
        return 'Midiendo ping...';
      case 'download':
        return 'Midiendo descarga...';
      case 'upload':
        return 'Midiendo subida...';
      case 'completed':
        return 'Prueba completada';
      case 'failed':
        return 'No se pudo completar la prueba';
      case 'aborted':
        return 'Prueba cancelada';
      default:
        return 'Listo para iniciar';
    }
  }

  progressPercent(snapshot: SpeedTestSnapshot): number {
    switch (snapshot.status) {
      case 'ping':
        return snapshot.pingProgress * 100;
      case 'download':
        return snapshot.downloadProgress * 100;
      case 'upload':
        return snapshot.uploadProgress * 100;
      default:
        return 0;
    }
  }

  isStartDisabled(): boolean {
    return this.linkChecking || !this.canStart;
  }

  showRetryVerification(snapshot: SpeedTestSnapshot): boolean {
    return !this.isBusy(snapshot) && !this.linkChecking && !this.canStart;
  }

  showStartButton(snapshot: SpeedTestSnapshot): boolean {
    return this.isBusy(snapshot) || this.canStart || !this.linkChecking;
  }

  showPhaseLabel(snapshot: SpeedTestSnapshot): boolean {
    if (this.isBusy(snapshot) || snapshot.status === 'completed' || snapshot.status === 'failed' || snapshot.status === 'aborted') {
      return true;
    }

    return this.canStart;
  }

  showActionsBar(snapshot: SpeedTestSnapshot): boolean {
    return (
      this.showStartButton(snapshot) ||
      this.showRetryVerification(snapshot) ||
      this.showPhaseLabel(snapshot) ||
      snapshot.status === 'completed'
    );
  }

  isLinkInactive(): boolean {
    if (this.linkChecking) {
      return false;
    }

    const response = this.linkStatusGate.lastResponse;
    return response?.state === 'fresh' && response.data?.status === 'inactive';
  }

  showMeasurementPanel(): boolean {
    return this.canStart;
  }

  linkStatusMessage(): string {
    if (this.linkChecking) {
      return 'Comprobando el enlace de la I.E.…';
    }
    if (this.canStart) {
      return 'Enlace operativo';
    }
    if (this.isLinkInactive()) {
      return 'El enlace de la I.E. está inactivo. No es posible realizar la prueba.';
    }

    return 'No se pudo verificar el estado del enlace. No es posible realizar la prueba.';
  }

  private loadLinkStatus(): void {
    this.linkChecking = true;
    this.servicesSub?.unsubscribe();
    this.servicesSub = this.api.customer.getInstitutionalServices().subscribe({
      next: (services) => {
        this.clientServiceId = services?.[0]?.clientServiceId ?? null;
        this.runVerify();
      },
      error: () => {
        this.clientServiceId = null;
        this.runVerify();
      },
    });
  }

  private runVerify(onPassed?: () => void): void {
    this.verifySub?.unsubscribe();
    this.linkChecking = true;
    this.verifySub = this.linkStatusGate.verify(this.clientServiceId).subscribe({
      next: (ok) => {
        this.canStart = ok;
        this.linkChecking = false;
        if (ok) {
          onPassed?.();
        }
      },
      error: () => {
        this.canStart = false;
        this.linkChecking = false;
      },
    });
  }

  private loadNetworkContext(): void {
    if (environment.SPEED_TEST_MODE === 'scenario') {
      this.networkContextLoading = true;
      this.networkContextSub = timer(1000).subscribe(() => {
        this.networkContext = {
          ip: '191.97.49.130',
          ipVersion: 4,
          asn: null,
          asnLabel: null,
          isp: '',
          ispRaw: null,
          ispSource: 'unknown',
          lookupSource: 'ipinfo',
        };
        this.networkContextLoading = false;
      });
      return;
    }

    this.networkContextLoading = true;

    this.networkContextSub = this.networkContextService
      .getContext()
      .pipe(
        finalize(() => {
          this.networkContextLoading = false;
        }),
      )
      .subscribe({
        next: (context: NetworkContext) => {
          this.networkContext = context;
        },
        error: () => {
          this.networkContext = null;
        },
      });
  }
}
